import { useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';

import { AngleDown, AngleUp } from '@assets/icons';
import { Divider, Spinner } from '@components/index';

import { ActionChips } from './ActionChips';
import { SearchItems } from './SearchItems';
import { SelectLabelItem } from './SelectLabelItem';
import { ContentModalActionType, Label, LabelItem } from './types';

type Props = {
  label?: string;
  items: LabelItem[];
  showChips?: boolean;
  isClientSearch?: boolean;
  handleSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectOption: (option: any, isRadioButtonDeSelectEnabled?: boolean) => void;
  selectedOptions?: Record<string, any>;
  onClearAllOptions?: () => void;
  isLoadingOptions?: boolean;
  fetchMoreOptions?: () => void;
  optionsHasNextPage?: boolean;
  shouldClearSearch?: boolean;
  handleActionClick: (data: Label, action: ContentModalActionType) => void;
  isFilterMode?: boolean;
  isAssetInherited?: boolean;
  isAssetLocked?: boolean;
};

const ChipListWithSearch = ({
  items,
  label,
  showChips = true,
  isClientSearch = true,
  handleSearch,
  onSelectOption,
  selectedOptions = {},
  onClearAllOptions,
  isLoadingOptions,
  fetchMoreOptions = () => {},
  optionsHasNextPage = false,
  handleActionClick,
  isFilterMode = false,
  isAssetInherited = false,
  isAssetLocked = false,
}: Props) => {
  const [options, setOptions] = useState(items);
  const [searchInput, setSearchInput] = useState('');
  const [accordionState, setAccordionState] = useState({
    inheritedLabels: false,
    workspaceLabels: true,
  });

  const { inheritedLabels, workspaceLabels } = useMemo(() => {
    return {
      inheritedLabels: options.filter(item => item.parentId),
      workspaceLabels: options.filter(item => !item.parentId),
    };
  }, [options]);

  const toggleAccordion = (section: keyof typeof accordionState) => {
    setAccordionState(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Reset options when items change (unless user is searching client-side)
  useEffect(() => {
    if (isEmpty(searchInput) || !isClientSearch) {
      setOptions(items);
    }
  }, [items, isClientSearch, searchInput]);

  const chipItems = Object.values(selectedOptions || {});

  const handleOptionSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setSearchInput(searchTerm);

    if (isClientSearch) {
      setOptions(
        items.filter(item =>
          item?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()),
        ),
      );
    } else {
      handleSearch?.(e);
    }
  };

  const LabelSection = ({
    title,
    sectionKey,
    labels,
    showAccordion,
    isAssetLocked,
  }: {
    title: string;
    sectionKey: keyof typeof accordionState;
    labels: LabelItem[];
    showAccordion: boolean;
    isAssetLocked: boolean;
  }) => {
    const isInheritedLabel = sectionKey === 'inheritedLabels';

    if (labels.length === 0) return null;

    // If only one section → render labels directly
    if (!showAccordion) {
      return (
        <div className="space-y-1">
          {labels.map(item => (
            <SelectLabelItem
              key={item.id}
              item={item}
              onSelectOption={onSelectOption}
              isSelected={!!selectedOptions[item.id]}
              searchInput={searchInput}
              handleActionClick={handleActionClick}
              disableLabel={
                isAssetLocked ||
                (isInheritedLabel && isAssetInherited && !isFilterMode)
              }
              isFilterMode={isFilterMode}
            />
          ))}
        </div>
      );
    }

    // Otherwise render accordion section
    return (
      <div className="mb-2">
        <div
          className="px-2 py-1 gap-2 text-sm text-text-primary mb-1 cursor-pointer flex items-center"
          onClick={() => toggleAccordion(sectionKey)}
        >
          {accordionState[sectionKey] ? (
            <AngleUp className="w-4 h-4" />
          ) : (
            <AngleDown className="w-4 h-4" />
          )}
          <span>{title}</span>
        </div>

        {accordionState[sectionKey] && (
          <div className="space-y-1">
            {labels.map(item => (
              <SelectLabelItem
                key={item.id}
                item={item}
                onSelectOption={onSelectOption}
                isSelected={!!selectedOptions[item.id]}
                searchInput={searchInput}
                handleActionClick={handleActionClick}
                disableLabel={
                  isAssetLocked ||
                  (isInheritedLabel && isAssetInherited && !isFilterMode)
                }
                isFilterMode={isFilterMode}
              />
            ))}
          </div>
        )}
        <Divider />
      </div>
    );
  };

  const multipleSections =
    inheritedLabels.length > 0 && workspaceLabels.length > 0;

  return (
    <>
      <SearchItems
        handleSearch={handleOptionSearch}
        searchInput={searchInput}
      />

      {showChips && chipItems.length > 0 && (
        <ActionChips
          items={chipItems}
          onRemoveChip={onSelectOption}
          onClearAllChips={onClearAllOptions}
          isFilterMode={isFilterMode}
          isAssetInherited={isAssetInherited}
          isAssetLocked={isAssetLocked}
        />
      )}

      <div
        id="scrollableDiv"
        className="flex flex-col gap-2 h-fit max-h-60 overflow-auto w-full"
      >
        {isLoadingOptions && (
          <div className="flex items-center">
            <Spinner />
          </div>
        )}

        {!isLoadingOptions && options.length === 0 && (
          <div className="text-sm text-text-secondary mt-2 flex items-center justify-center">
            {`No ${label?.toLowerCase() || 'items'} found`}
          </div>
        )}

        {!isLoadingOptions && options.length > 0 && (
          <InfiniteScroll
            dataLength={options.length}
            next={fetchMoreOptions}
            hasMore={Boolean(optionsHasNextPage)}
            loader={<Spinner />}
            className="w-full h-full py-2"
            height={240}
            scrollableTarget="scrollableDiv"
          >
            <LabelSection
              title="Inherited labels"
              sectionKey="inheritedLabels"
              labels={inheritedLabels}
              showAccordion={multipleSections}
              isAssetLocked={isAssetLocked}
            />
            <LabelSection
              title="Labels from this workspace"
              sectionKey="workspaceLabels"
              labels={workspaceLabels}
              showAccordion={multipleSections}
              isAssetLocked={isAssetLocked}
            />
          </InfiniteScroll>
        )}
      </div>
    </>
  );
};

export default ChipListWithSearch;
